import { useEffect, useMemo, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  KeyRound, Server, Save, X, RefreshCw,
  Eye, EyeOff, Copy, Ban, Play, ShieldOff, Lock, Trash2,
} from "lucide-react";

function StatusPill({ status }) {
  const map = {
    running: ["bg-emerald-500/20 text-emerald-400 border-emerald-500/30", "● online"],
    provisioning: ["bg-amber-500/20 text-amber-400 border-amber-500/30", "◐ provisionando"],
    stopped: ["bg-red-500/20 text-red-400 border-red-500/30", "○ parada"],
    suspended: ["bg-orange-500/20 text-orange-400 border-orange-500/30", "! suspensa"],
    terminated: ["bg-zinc-500/20 text-zinc-400 border-zinc-500/30", "× encerrada"],
  };
  const [cls, label] = map[status] || map.terminated;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border font-mono text-xs uppercase ${cls}`}>{label}</span>;
}

function genRandomPassword(len = 16) {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let p = "";
  for (let i = 0; i < len; i++) p += alphabet[Math.floor(Math.random() * alphabet.length)];
  return p;
}

// ---------- Password cell: masked with reveal + copy --------------------------
function PasswordCell({ value }) {
  const [visible, setVisible] = useState(false);
  if (!value) return <span className="text-zinc-600">—</span>;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Senha copiada");
    } catch {
      toast.error("Falha ao copiar");
    }
  };
  return (
    <div className="flex items-center gap-1.5 font-mono text-xs">
      <span className="tabular-nums select-all">
        {visible ? value : "•".repeat(Math.min(value.length, 10))}
      </span>
      <button
        onClick={() => setVisible((v) => !v)}
        className="p-1 rounded-sm hover:bg-white/10 text-zinc-500 hover:text-white"
        title={visible ? "Ocultar" : "Mostrar"}
        data-testid="admin-pw-toggle"
      >
        {visible ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
      </button>
      <button
        onClick={copy}
        className="p-1 rounded-sm hover:bg-white/10 text-zinc-500 hover:text-white"
        title="Copiar"
        data-testid="admin-pw-copy"
      >
        <Copy className="h-3 w-3" />
      </button>
    </div>
  );
}

// ---------- Modal: reset root password ---------------------------------------
function ResetPasswordModal({ vps, onClose, onSaved }) {
  const [pw, setPw] = useState(genRandomPassword());
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Mínimo 8 caracteres"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/vps/${vps.vps_id}/password`, { root_password: pw });
      if (data?.applied_live === false) {
        toast.warning("Senha salva no banco, mas não foi aplicada ao vivo (VM offline ou sem guest-agent).");
      } else {
        toast.success("Senha resetada com sucesso.");
      }
      onSaved(pw);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao resetar senha");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="w-full max-w-lg bg-[#121214] border border-white/10 rounded-sm" data-testid="reset-pw-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">// admin · reset</div>
            <div className="font-heading text-lg font-bold">Resetar senha root</div>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs font-mono text-zinc-500 border border-white/10 bg-black/20 rounded-sm p-3 leading-relaxed">
            VPS: <span className="text-white">{vps.hostname || vps.vps_id}</span><br />
            VMID: <span className="text-white">{vps.virtualizor_vps_id || "—"}</span> · IP: <span className="text-white">{vps.ipv4 || "—"}</span><br />
            Status: <span className="text-white">{vps.status}</span>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Nova senha root</label>
            <div className="flex gap-2">
              <input
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="flex-1 rounded-sm bg-[#09090B] border border-white/10 px-3 py-2 font-mono text-sm text-white"
                data-testid="reset-pw-input"
                autoComplete="new-password"
              />
              <button type="button" onClick={() => setPw(genRandomPassword())} className="px-3 py-2 rounded-sm border border-white/10 text-xs font-mono uppercase tracking-widest hover:bg-white hover:text-black" title="Gerar nova">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="text-[10px] font-mono text-zinc-500 mt-1">
              Aplica ao vivo via QEMU guest-agent (se a VM estiver ligada). Também salva no banco.
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-white/10">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-sm border border-white/10 text-xs font-mono uppercase tracking-widest hover:bg-white/5">Cancelar</button>
          <Button type="submit" disabled={busy} className="rounded-sm bg-gradient-to-r from-emerald-500 to-cyan-500 text-white hover:opacity-90" data-testid="reset-pw-save">
            <Save className="h-4 w-4 mr-2" />{busy ? "Salvando..." : "Resetar senha"}
          </Button>
        </div>
      </form>
    </div>
  );
}

// Modal to fill in VMID / IP / password for a manual-provisioning VPS
function ManualProvisionModal({ vps, onClose, onSaved }) {
  const [vmid, setVmid] = useState(vps?.virtualizor_vps_id || "");
  const [ip, setIp] = useState(vps?.ipv4 || "");
  const [hostname, setHostname] = useState(vps?.hostname || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const genPassword = () => setPassword(genRandomPassword());

  useEffect(() => {
    if (!password) genPassword();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!vmid.trim() || !ip.trim() || !password.trim()) {
      toast.error("VMID, IP e senha são obrigatórios");
      return;
    }
    if (password.length < 8) { toast.error("Senha precisa de pelo menos 8 caracteres"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/vps/${vps.vps_id}/manual-provision`, {
        virtualizor_vps_id: vmid.trim(),
        ipv4: ip.trim(),
        root_password: password,
        hostname: hostname.trim() || undefined,
      });
      toast.success("VPS marcada como pronta. Cliente foi notificado por email.");
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="manual-prov-modal">
      <form onSubmit={submit} className="w-full max-w-lg bg-[#121214] border border-white/10 rounded-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">// manual · entrega</div>
            <div className="font-heading text-lg font-bold">Preencher dados da VPS</div>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs font-mono text-zinc-500 border border-white/10 bg-black/20 rounded-sm p-3 leading-relaxed">
            Cliente: <span className="text-white">{vps.user_email || vps.user_id}</span><br />
            Plano: <span className="text-white">{vps.cpu_cores}v · {vps.ram_gb}G · {vps.ssd_gb}G · {vps.location}</span><br />
            vps_id: <span className="text-white">{vps.vps_id}</span>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">VMID (Proxmox ID)</label>
            <input value={vmid} onChange={(e) => setVmid(e.target.value)} placeholder="ex: 300" className="w-full rounded-sm bg-[#09090B] border border-white/10 px-3 py-2 font-mono text-sm text-white" data-testid="mp-vmid" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">IP público IPv4</label>
            <input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="ex: 200.150.10.42" className="w-full rounded-sm bg-[#09090B] border border-white/10 px-3 py-2 font-mono text-sm text-white" data-testid="mp-ip" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Hostname (opcional)</label>
            <input value={hostname} onChange={(e) => setHostname(e.target.value)} placeholder="ex: vps-cliente" className="w-full rounded-sm bg-[#09090B] border border-white/10 px-3 py-2 font-mono text-sm text-white" data-testid="mp-hostname" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Senha root</label>
            <div className="flex gap-2">
              <input value={password} onChange={(e) => setPassword(e.target.value)} className="flex-1 rounded-sm bg-[#09090B] border border-white/10 px-3 py-2 font-mono text-sm text-white" data-testid="mp-pass" />
              <button type="button" onClick={genPassword} className="px-3 py-2 rounded-sm border border-white/10 text-xs font-mono uppercase tracking-widest hover:bg-white hover:text-black">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="text-[10px] font-mono text-zinc-500 mt-1">Mínimo 8 caracteres.</div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-white/10">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-sm border border-white/10 text-xs font-mono uppercase tracking-widest hover:bg-white/5">Cancelar</button>
          <Button type="submit" disabled={busy} className="rounded-sm bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white hover:opacity-90" data-testid="mp-save">
            <Save className="h-4 w-4 mr-2" />{busy ? "Salvando..." : "Marcar como pronta"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function AdminVpsPage() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState({});
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [target, setTarget] = useState(null);        // manual-provision modal
  const [pwTarget, setPwTarget] = useState(null);    // reset-password modal
  const [busyId, setBusyId] = useState(null);        // per-VPS action spinner

  const reload = async () => {
    const [vpsRes, usersRes] = await Promise.all([
      api.get("/admin/vps"),
      api.get("/admin/clients").catch(() => ({ data: [] })),
    ]);
    setItems(vpsRes.data);
    const dict = {};
    for (const u of usersRes.data || []) dict[u.user_id] = u;
    setUsers(dict);
  };

  useEffect(() => { reload(); }, []);

  const suspend = async (v) => {
    if (!window.confirm(`Suspender/bloquear a VPS ${v.hostname || v.vps_id}?\n\nA VM será desligada no Proxmox e o cliente verá o status "suspensa".`)) return;
    setBusyId(v.vps_id);
    try {
      await api.post(`/admin/vps/${v.vps_id}/suspend`);
      toast.success("VPS suspensa");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao suspender");
    } finally { setBusyId(null); }
  };

  const unsuspend = async (v) => {
    setBusyId(v.vps_id);
    try {
      await api.post(`/admin/vps/${v.vps_id}/unsuspend`);
      toast.success("VPS reativada");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao reativar");
    } finally { setBusyId(null); }
  };

  const retryBrazil = async (v) => {
    setBusyId(v.vps_id);
    try {
      await api.post(`/admin/vps/${v.vps_id}/retry-delivery`);
      toast.success("Nova tentativa de entrega iniciada");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível reprocessar a entrega");
    } finally { setBusyId(null); }
  };

  const setRenewalPrice = async (v) => {
    const current = v.renewal_price_brl == null ? "" : String(v.renewal_price_brl);
    const entered = window.prompt(
      "Preço mensal exclusivo desta VPS em R$ (deixe em branco para usar o preço do plano):",
      current,
    );
    if (entered === null) return;
    const normalized = entered.trim().replace(",", ".");
    const renewal_price_brl = normalized === "" ? null : Number(normalized);
    if (renewal_price_brl !== null && (!Number.isFinite(renewal_price_brl) || renewal_price_brl < 0.01)) {
      toast.error("Informe um valor maior que R$ 0,00.");
      return;
    }
    setBusyId(v.vps_id);
    try {
      await api.patch(`/admin/vps/${v.vps_id}/renewal-price`, { renewal_price_brl });
      toast.success(renewal_price_brl === null
        ? "Preço mensal voltou ao valor do plano."
        : `Preço mensal desta VPS atualizado para R$ ${renewal_price_brl.toFixed(2)}.`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao atualizar o preço mensal");
    } finally { setBusyId(null); }
  };

  const deleteVps = async (v) => {
    const u = users[v.user_id];
    const ok = window.confirm(
      `Excluir a VPS ${v.hostname || v.vps_id} de ${u?.name || u?.email || v.user_id}?\n\n` +
      `A VM será desligada e removida do Proxmox. O IP só será liberado depois que o provedor confirmar a remoção. ` +
      `As faturas permanecem no histórico. Esta ação não pode ser desfeita.`
    );
    if (!ok) return;
    setBusyId(v.vps_id);
    try {
      const { data } = await api.delete(`/admin/vps/${v.vps_id}`);
      if (data?.ip_released === false) {
        toast.warning("VPS encerrada; o IP continua reservado. Tente liberar novamente na aba Excluídas.");
      } else {
        toast.success("VPS encerrada e removida da lista ativa.");
      }
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir VPS");
    } finally { setBusyId(null); }
  };

  const retryIpRelease = async (v) => {
    setBusyId(v.vps_id);
    try {
      const { data } = await api.delete(`/admin/vps/${v.vps_id}`);
      if (data?.ip_released) toast.success("IP liberado após nova confirmação do provedor.");
      else toast.warning("O IP continua reservado; tente novamente mais tarde.");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao liberar o IP");
    } finally { setBusyId(null); }
  };

  const activeItems = items.filter((v) => !v.admin_deleted);
  const deletedItems = items.filter((v) => v.admin_deleted);
  const pendingCount = activeItems.filter(
    (v) => (v.manual_provisioning && !v.ipv4) || (v.provider === "brasil" && v.provision_state === "failed")
  ).length;
  const suspendedCount = activeItems.filter((v) => v.status === "suspended").length;

  const shown = useMemo(() => {
    let arr = filter === "deleted" ? items.filter((v) => v.admin_deleted) : items.filter((v) => !v.admin_deleted);
    if (filter === "pending") arr = arr.filter((v) => (v.manual_provisioning && !v.ipv4) || (v.provider === "brasil" && v.provision_state === "failed"));
    else if (filter === "manual") arr = arr.filter((v) => v.manual_provisioning);
    else if (filter === "suspended") arr = arr.filter((v) => v.status === "suspended");
    else if (filter === "running") arr = arr.filter((v) => v.status === "running");
    const s = search.trim().toLowerCase();
    if (s) {
      arr = arr.filter((v) => {
        const u = users[v.user_id];
        const blob = [
          v.hostname, v.ipv4, v.virtualizor_vps_id, v.vps_id,
          u?.email, u?.name,
        ].filter(Boolean).join(" ").toLowerCase();
        return blob.includes(s);
      });
    }
    return arr;
  }, [items, filter, search, users]);

  return (
    <div className="space-y-6" data-testid="admin-vps-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">// admin · vps</div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter">Servidores</h1>
          <div className="text-xs font-mono text-zinc-500 mt-1">
            {items.length} VPS · {suspendedCount} suspensa{suspendedCount !== 1 ? "s" : ""} · {pendingCount} aguardando entrega
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="buscar por cliente, IP, hostname, VMID..."
            className="w-64 rounded-sm bg-[#09090B] border border-white/10 px-3 py-2 font-mono text-xs text-white"
            data-testid="admin-vps-search"
          />
          <button onClick={reload} className="text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-sm border border-white/10 hover:bg-white/5" data-testid="admin-vps-reload">
            <RefreshCw className="h-3.5 w-3.5 inline mr-2" />Recarregar
          </button>
        </div>
      </div>

      {pendingCount > 0 && (
        <div className="border border-pink-500/40 bg-pink-500/5 rounded-sm p-4 flex items-center justify-between" data-testid="pending-manual-alert">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-pink-400">// ação necessária</div>
            <div className="text-white mt-1">
              <span className="font-bold">{pendingCount}</span> VPS aguardando entrega manual (planos Brasil)
            </div>
            <div className="text-xs text-zinc-400 mt-1">Crie a VM no Proxmox e clique em "Preencher" para notificar o cliente.</div>
          </div>
          <Button onClick={() => setFilter("pending")} className="rounded-sm bg-pink-500 hover:bg-pink-600 text-white">
            Ver pendentes →
          </Button>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {[
          { k: "all",       l: `Todas (${activeItems.length})` },
          { k: "running",   l: `Online (${activeItems.filter((v) => v.status === "running").length})` },
          { k: "suspended", l: `Suspensas (${suspendedCount})` },
          { k: "pending",   l: `Pendentes manual (${pendingCount})` },
          { k: "manual",    l: `Todas manual (${activeItems.filter((v) => v.manual_provisioning).length})` },
          { k: "deleted",   l: `Excluídas (${deletedItems.length})` },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`admin-vps-filter-${f.k}`}
            className={`text-xs font-mono uppercase tracking-widest px-3 py-2 rounded-sm border ${
              filter === f.k
                ? "bg-white text-black border-white"
                : "border-white/10 text-zinc-400 hover:text-white hover:border-white/40"
            }`}
          >{f.l}</button>
        ))}
      </div>

      <div className="border border-white/10 bg-[#121214] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs font-mono uppercase tracking-widest text-zinc-500">
            <tr className="border-b border-white/10">
              <th className="text-left px-4 py-3">Cliente</th>
              <th className="text-left px-4 py-3">Hostname</th>
              <th className="text-left px-4 py-3">IP</th>
              <th className="text-left px-4 py-3">VMID</th>
              <th className="text-left px-4 py-3">Senha root</th>
              <th className="text-left px-4 py-3">Specs</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((v) => {
              const u = users[v.user_id];
              const isPending = v.manual_provisioning && !v.ipv4;
              const isSuspended = v.status === "suspended";
              const isTerminated = v.status === "terminated";
              const isDeleted = Boolean(v.admin_deleted);
              return (
                <tr
                  key={v.vps_id}
                  className={`border-b border-white/5 hover:bg-white/5 ${isPending ? "bg-pink-500/5" : ""} ${isSuspended ? "bg-orange-500/5" : ""}`}
                  data-testid={`admin-vps-row-${v.vps_id}`}
                >
                  <td className="px-4 py-3">
                    <div className="text-sm">{u?.name || <span className="text-zinc-500">—</span>}</div>
                    <div className="text-xs font-mono text-zinc-500">{u?.email || v.user_id}</div>
                    <div className="text-[10px] font-mono text-zinc-600">{fmtDate(v.created_at)}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {v.hostname || <span className="text-zinc-600">(sem hostname)</span>}
                    {v.manual_provisioning && (
                      <div className="mt-0.5"><span className="inline-flex items-center px-1.5 py-0.5 rounded-sm border border-pink-500/40 bg-pink-500/10 text-pink-300 text-[10px] font-mono uppercase">🇧🇷 manual</span></div>
                    )}
                    {v.provider === "brasil" && (
                      <div className="mt-0.5 text-[10px] text-amber-300 font-mono">Brasil · {v.provision_state === "failed" ? "falha na entrega" : v.provision_state}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {v.ipv4 || <span className="text-pink-400">(pendente)</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{v.virtualizor_vps_id || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-4 py-3">
                    <PasswordCell value={v.root_password} />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{v.cpu_cores}v · {v.ram_gb}G · {v.ssd_gb}G
                    {v.renewal_price_brl != null && <div className="text-[10px] text-emerald-300 mt-0.5">Renovação: R$ {Number(v.renewal_price_brl).toFixed(2)}/mês</div>}
                  </td>
                  <td className="px-4 py-3"><StatusPill status={v.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      {!isDeleted && v.manual_provisioning && !isTerminated && (
                        <button
                          onClick={() => setTarget({ ...v, user_email: u?.email })}
                          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border ${
                            isPending
                              ? "bg-pink-500 hover:bg-pink-600 border-pink-500 text-white"
                              : "border-white/10 hover:bg-white/5"
                          }`}
                          data-testid={`fill-btn-${v.vps_id}`}
                          title="Preencher/editar dados manuais"
                        >
                          <KeyRound className="h-3 w-3 inline mr-1" />
                          {isPending ? "Preencher" : "Editar"}
                        </button>
                      )}
                      {!isDeleted && v.provider === "brasil" && v.provision_state === "failed" && (
                        <button onClick={() => retryBrazil(v)} disabled={busyId === v.vps_id}
                          className="text-[10px] font-mono uppercase px-2 py-1 border border-amber-500/40 text-amber-300 disabled:opacity-50"
                          title={v.provision_error || "Reprocessar entrega"}>Tentar novamente</button>
                      )}
                      {!isDeleted && !isTerminated && v.ipv4 && v.provider !== "brasil" && (
                        <button
                          onClick={() => setPwTarget(v)}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500 hover:text-black"
                          title="Resetar senha root"
                          data-testid={`reset-pw-btn-${v.vps_id}`}
                        >
                          <Lock className="h-3 w-3 inline mr-1" />Resetar
                        </button>
                      )}
                      {!isDeleted && !isTerminated && !v.admin_test_vps && !v.complimentary_partner && (
                        <button onClick={() => setRenewalPrice(v)} disabled={busyId === v.vps_id || Boolean(v.pending_renewal_invoice_id)} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border border-sky-500/40 text-sky-300 hover:bg-sky-500 hover:text-black disabled:opacity-50" title={v.pending_renewal_invoice_id ? "Cancele ou quite a fatura de renovação antes de alterar" : "Definir ou remover preço mensal exclusivo"} data-testid={`renewal-price-btn-${v.vps_id}`}>R$/mês</button>
                      )}
                      {!isDeleted && !isTerminated && !isSuspended && (
                        <button
                          onClick={() => suspend(v)}
                          disabled={busyId === v.vps_id}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border border-orange-500/40 text-orange-300 hover:bg-orange-500 hover:text-black disabled:opacity-50"
                          title="Suspender/bloquear VPS"
                          data-testid={`suspend-btn-${v.vps_id}`}
                        >
                          <Ban className="h-3 w-3 inline mr-1" />Suspender
                        </button>
                      )}
                      {!isDeleted && isSuspended && (
                        <button
                          onClick={() => unsuspend(v)}
                          disabled={busyId === v.vps_id}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500 hover:text-black disabled:opacity-50"
                          title="Reativar VPS"
                          data-testid={`unsuspend-btn-${v.vps_id}`}
                        >
                          <Play className="h-3 w-3 inline mr-1" />Reativar
                        </button>
                      )}
                      {!isDeleted && (
                        <button
                          onClick={() => deleteVps(v)}
                          disabled={busyId === v.vps_id}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border border-red-500/40 text-red-300 hover:bg-red-500 hover:text-white disabled:opacity-50"
                          title="Excluir VPS e encerrar a VM"
                          data-testid={`delete-vps-btn-${v.vps_id}`}
                        >
                          {busyId === v.vps_id
                            ? <RefreshCw className="h-3 w-3 inline mr-1 animate-spin" />
                            : <Trash2 className="h-3 w-3 inline mr-1" />}
                          Excluir
                        </button>
                      )}
                      {isDeleted && v.ip_release_pending && (
                        <button
                          onClick={() => retryIpRelease(v)}
                          disabled={busyId === v.vps_id}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black disabled:opacity-50"
                          title="Confirmar que a VM foi removida e liberar o IP"
                          data-testid={`retry-ip-release-btn-${v.vps_id}`}
                        >
                          {busyId === v.vps_id
                            ? <RefreshCw className="h-3 w-3 inline mr-1 animate-spin" />
                            : <RefreshCw className="h-3 w-3 inline mr-1" />}
                          Liberar IP
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {shown.length === 0 && (
              <tr>
                <td colSpan={8} className="p-10 text-center text-zinc-500">
                  <Server className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  Nenhuma VPS encontrada.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {target && (
        <ManualProvisionModal
          vps={target}
          onClose={() => setTarget(null)}
          onSaved={() => { setTarget(null); reload(); }}
        />
      )}
      {pwTarget && (
        <ResetPasswordModal
          vps={pwTarget}
          onClose={() => setPwTarget(null)}
          onSaved={() => { setPwTarget(null); reload(); }}
        />
      )}
    </div>
  );
}
